// Implements: F-UIX-010
/**
 * The cache the whole application shares, and its retry policy.
 *
 * The policy is `retry: false`, and that is not "no retries": `httpClient`
 * already retried a network failure and a 5xx twice (DD6), so a second layer
 * here would multiply attempts by three and turn one unreachable request into
 * nine. Retries live in one place, the same way `fetch` does.
 *
 * `throwOnError` stays false: a failed query is a value a container renders as
 * `ErrorState` (F-UIX-010), not an exception that unmounts the tree.
 */

import { QueryClient } from "@tanstack/react-query";

/** How long a fetched value is served without going back to the network. */
export const DEFAULT_STALE_TIME_MS = 30_000;

/** Build a client. Tests build their own; the application builds one. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: DEFAULT_STALE_TIME_MS,
        // A window regaining focus is not new information about the session,
        // and refetching on every focus is how a quiet tab becomes traffic.
        refetchOnWindowFocus: false,
      },
      mutations: {
        // A mutation that failed did something, or did not; repeating it is a
        // decision for the screen that owns it, never a default.
        retry: false,
      },
    },
  });
}

/** The one instance `src/app/providers.tsx` mounts. */
export const queryClient = createQueryClient();
